import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { Button } from "./ui/button";

const DISMISS_KEY = "flowtasks_install_dismissed_at";
const DISMISS_DAYS = 14;

/**
 * Auto-prompts the user to install the PWA after 2+ visits.
 * - Hooks into the `beforeinstallprompt` event (Chrome, Edge, Brave, Android).
 * - On iOS Safari (where the event doesn't fire), shows a manual hint instead.
 * - Dismissals stick for 14 days.
 */
export default function InstallPrompt() {
  const [event, setEvent] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    const visits = parseInt(localStorage.getItem("flowtasks_visits") || "0", 10);
    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    const dismissedRecently = Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000;
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone || dismissedRecently || visits < 2) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setEvent(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari fallback (no beforeinstallprompt event)
    const ua = window.navigator.userAgent || "";
    const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    if (isIos) {
      setIosHint(true);
      setShow(true);
    }

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  const install = async () => {
    if (!event) return;
    event.prompt();
    const choice = await event.userChoice;
    if (choice.outcome === "accepted") {
      setShow(false);
    } else {
      dismiss();
    }
  };

  if (!show) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed left-4 right-4 sm:left-auto sm:right-6 z-50 max-w-sm rounded-xl border border-primary/30 bg-card shadow-2xl p-4 anim-fade-up"
      style={{ bottom: "max(1rem, calc(env(safe-area-inset-bottom) + 0.5rem))" }}
    >
      <button
        data-testid="install-prompt-close"
        onClick={dismiss}
        className="absolute top-2 right-2 p-1.5 rounded-md text-muted-foreground hover:bg-secondary"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 shrink-0 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
          <Download size={18} />
        </div>
        <div className="flex-1 min-w-0 pr-5">
          <div className="font-display font-bold text-[15px] leading-tight">Install FlowTasks360</div>
          {iosHint ? (
            <p className="text-xs text-muted-foreground mt-1">
              Tap the share icon in Safari then "Add to Home Screen" for the full app experience.
            </p>
          ) : (
            <p className="text-xs text-muted-foreground mt-1">
              Add to your home screen for one-tap access and a native app feel.
            </p>
          )}
          {!iosHint && (
            <div className="mt-3 flex gap-2">
              <Button size="sm" onClick={install} data-testid="install-prompt-install-btn">
                Install
              </Button>
              <Button size="sm" variant="ghost" onClick={dismiss} data-testid="install-prompt-later-btn">
                Maybe later
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
